import { BaseQueryApi, BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { AppState } from '@redux/store'
import { config } from '@config/config'
import { AuthToken, AuthTokenArgs, Customer } from '@typesApp/Session'
import { shouldRefreshToken } from '@utils/authToken'
import {
  createSessionExpiredFetchError,
  createSessionTransitionedFetchError,
  isConflictError,
  isUnauthorizedError,
} from '@utils/errorUtil'
import { appEventEmitter } from '@utils/appEventEmitter'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { authTokenError, authTokenReady, resetSessionTransitioned } from '@redux/slices/connectorSlice'
import { dbPrepareHeaders } from './prepareHeaders'
import { authApi } from './auth'
import { ApiCache } from './apiCache'

const mutex = new Mutex()

export const dbBaseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const baseUrl = config.getApiHost()
  const dbBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: dbPrepareHeaders,
  })

  const relativeUrl = typeof args === 'string' ? args : args.url
  const url = `${baseUrl}${relativeUrl}`
  const apiCache = new ApiCache(api.endpoint)

  // fetch from cache if available
  if (apiCache.isCacheable) {
    const cachedResult = await apiCache.fetchFromCache(url)
    if (cachedResult) {
      return cachedResult
    }
  }

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()

  let result = await dbBaseQuery(args, api, extraOptions)

  if (shouldRefreshToken(result, api.getState() as AppState)) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()

      try {
        const state = api.getState() as AppState

        // if session has transitioned (e.g. from authenticated to guest), no need to retry fetch
        if (isSessionTransitioned(api)) {
          return handleSessionTransitioned(api)
        }

        // use our internal session id to check if session is still valid
        const sessionId = state.connector.authTokenSessionId
        const customer = state.connector.sessionCustomer
        const anonymousUserId = state.connector.environment?.anonymousUserId

        // if customer id is not valid or changed, the session is no longer valid
        if (!sessionId || !customer?.id || !isCustomerNumberValid(customer?.id, anonymousUserId)) {
          appEventEmitter.sessionExpired()
          const error = createSessionExpiredFetchError(
            `Invalid session id or customer number ${customer?.id ?? 'unknown'}, session expired?`
          )
          return { error }
        }

        // refetch token using current internal session id
        try {
          const authToken = await fetchTokenWithRetry(api, sessionId, customer)
          api.dispatch(authTokenReady({ authTokenSessionId: authToken.sessionId }))
        } catch (fetchError) {
          // session has expired if retry also fails with 401
          if (isUnauthorizedError(fetchError)) {
            // check again if session may have transitioned
            if (isSessionTransitioned(api)) {
              return handleSessionTransitioned(api)
            }

            appEventEmitter.sessionExpired()
            api.dispatch(authTokenError())
            const error = createSessionExpiredFetchError('Token has been revoked, session expired?')
            return { error }
          }

          api.dispatch(authTokenError())
          return { error: fetchError as FetchBaseQueryError }
        }

        // Retry original request with new token
        result = await dbBaseQuery(args, api, extraOptions)
      } finally {
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await dbBaseQuery(args, api, extraOptions)
    }
  }

  if (apiCache.isCacheable) {
    apiCache.putInCache(result)
  }

  return result
}

const isSessionTransitioned = (api: BaseQueryApi): boolean => {
  const state = api.getState() as AppState
  return Boolean(state.connector.isSessionTransitioned)
}

const handleSessionTransitioned = (api: BaseQueryApi): { error: FetchBaseQueryError } => {
  const state = api.getState() as AppState
  const previousId = state.connector.previousSession?.userId
  const currentId = state.connector.sessionCustomer?.id

  console.debug(
    `directbilling session transitioned from customer ${previousId ?? 'unknown'} to ${currentId ?? 'unknown'}`
  )

  api.dispatch(resetSessionTransitioned())
  const error = createSessionTransitionedFetchError(
    `Session transitioned from customer ${previousId ?? 'unknown'} to ${currentId ?? 'unknown'}`
  )
  return { error }
}

const fetchTokenWithRetry = async (
  api: BaseQueryApi,
  sessionId: string,
  customer: Customer
): Promise<AuthToken> | never => {
  const authTokenArgs: AuthTokenArgs = {
    sessionId,
    customerNumber: customer?.id,
    customerEmail: customer?.email,
  }

  if (!customer?.email) {
    delete authTokenArgs.customerEmail
  }

  // refetch token using current internal session id
  // if session has expired, fetch will fail with 401 otherwise fetch will return new internal session id
  console.debug('directbilling session - refetch auth token for customer=%s session=%s', customer.id, sessionId)

  // note: getAuthToken api has automatic retry on 409
  return await api.dispatch(authApi.endpoints.getAuthToken.initiate(authTokenArgs)).unwrap()
}
